import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalGuardConfiguration, MsalService, MSAL_GUARD_CONFIG } from '@azure/msal-angular';
import { AccountInfo,
         AuthenticationResult,
         AuthError,
         BrowserUtils,
         EventMessage,
         EventPayload,
         EventType,
         InteractionRequiredAuthError,
         InteractionStatus,
         PopupRequest,
         SilentRequest } from '@azure/msal-browser';
import { BehaviorSubject, defer, from, merge, Observable, of, throwError, timer } from 'rxjs';
import { catchError,
         distinctUntilChanged,
         distinctUntilKeyChanged,
         filter, map,mapTo, shareReplay, startWith, switchMap, take, tap } from 'rxjs/operators';
import { SESSION_POST_LOGIN_REDIRECT_URL } from './auth-config';
import { NotificationsService } from '../shared/notifications/notifications.service';

type EventMessageTyped<T extends EventPayload> = EventMessage & { payload: T };

@Injectable()
export class AuthService {
  /** The time in seconds before expiration when the access token should be renewed. */
  private accessTokenExpiringNotificationTime = 60;

  /** returns the last auth result or undefined it the last result was an error or the result is expired. */
  readonly authResult$ = defer(() => merge(
    this.successfulAuthResult$,
    this.deauthorizingEvents$.pipe(mapTo(undefined)),
  )).pipe(
    // we don't need a startWith since this service starts with a silent renew.
    switchMap(res => {
      if (!res || !res.expiresOn) { // it's shift that there is no expires on.
        return of(undefined);
      }
      // the token should've been renewed, if that renewal failed or didn't occur, then we unset the token.
      return timer(res.expiresOn).pipe(
        mapTo(undefined),
        startWith(res)
      )
    }),
    distinctUntilChanged(),
    shareReplay(1)
  );

  /** The current active account that is accompanized with current token. */
  readonly authorizedAccount$ = defer(() => this.activeAccountSubject).pipe(startWith(undefined), distinctUntilChanged(), shareReplay(1));

  /** Set everytime active account is set on msalInstace */
  private readonly activeAccountSubject = new BehaviorSubject<AccountInfo | undefined>(undefined);

  /** Not quite sure which events should be tracked so that the activeAccount gets unset. */
  private readonly deauthorizingEvents$ = this.msalBroadcastSvc.msalSubject$.pipe(
    filter((x) => x.eventType === EventType.LOGOUT_SUCCESS
      || x.eventType === EventType.LOGIN_FAILURE
      || x.eventType === EventType.ACQUIRE_TOKEN_FAILURE
      || x.eventType === EventType.SSO_SILENT_FAILURE
    )
  );

  /** Do we need this> */
  private readonly isBeingInteractedWith$ = this.msalBroadcastSvc.inProgress$.pipe(
    map((status) => status !== InteractionStatus.None),
    startWith(false)
  );

  /** stores the last token response. */
  private readonly successfulAuthResult$ = this.msalBroadcastSvc.msalSubject$.pipe(
    filter((x): x is EventMessageTyped<AuthenticationResult> => !x.error && !!(x.payload as Partial<AuthenticationResult>)?.accessToken),
    map(x => x.payload),
    distinctUntilKeyChanged('accessToken'),
    shareReplay(1)
  );


  /**
   * Tries to do silent authentication, and listens to authResult$ to set active account appropriately.
   */
  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalSvc: MsalService,
    private msalBroadcastSvc: MsalBroadcastService,
    private notifySvc: NotificationsService,
    private router: Router
  ) {

    // do nothing if we're in an iframe or popup.
    if (BrowserUtils.isInIframe() || BrowserUtils.isInPopup()) {
      return;
    }

    this.msalSvc.instance.enableAccountStorageEvents();


    // after a successful auth result keep track when it expires so that it can be acquired silently.
    this.successfulAuthResult$.pipe(
      map(res => res.expiresOn),
      filter((expiresOn): expiresOn is NonNullable<Date> => !!expiresOn),
      switchMap(expiresOn => {
        // automatically try to renew token with a timer.
        const renewalTime = new Date(expiresOn.valueOf() - this.accessTokenExpiringNotificationTime * 1000);
        return timer(renewalTime).pipe(
          switchMap(() => this.aquireTokenSilent(false).pipe(catchError(() => of(undefined)))),
        );
      })
    ).subscribe();

    // Msal passively tries to use a refresh token when a guard is hit or interceptor is use.
    // This will try to acquire token silently before doing anything else.
    this.isBeingInteractedWith$.pipe(
      filter(x => !x), // don't try to acquireTokenSilent while being interacted with.
      take(1),
      switchMap(() => this.aquireTokenSilent(false)),
      catchError(() => of(undefined)),
      // because authResult is being subscribed to after acquire token, we need to make sure we send the result so it can be replayed.
      switchMap(res => this.authResult$.pipe(startWith(res))),
      // keep the account in sync and makes sure that the current value of authResult$ is available for replay to any subscribers.
      tap(x => this.setActiveAccount(x?.account))
    ).subscribe();

    // notify if popups are blocked
    this.msalBroadcastSvc.msalSubject$.pipe(
      filter((x: EventMessage) => {
        const eventType = x.eventType;
        const error = x.error as Partial<AuthError> | undefined;
        const errorCode = error?.errorCode;

        const FAILURE_EVENTS = [
          'msal.loginFailure',      // ← EventType.LOGIN_FAILURE maps to this
          'msal.acquireTokenFailure' // ← EventType.ACQUIRE_TOKEN_FAILURE maps to this
        ] as const;

        return FAILURE_EVENTS.includes(eventType as any)
          && (['popup_window_error', 'empty_window_error'] as (string | undefined)[]).includes(errorCode);
      }),
      // eslint-disable-next-line max-len
      tap(() => this.notifySvc.addDanger('Cannot open login because popup windows are blocked.  Please unblock popups to login.', 'Login Error')),
      take(1)
    ).subscribe();
  }

  // eslint-disable-next-line max-len
  /** If there is a value is post login redirect url from MsalGuard, then this will redirect to it.  Clears it after so it won't be reused accidently. */
  doPostLoginRedirect(): Observable<boolean> {
    const loginRedirectUrl = sessionStorage.getItem(SESSION_POST_LOGIN_REDIRECT_URL);
    if (loginRedirectUrl) {
      sessionStorage.removeItem(SESSION_POST_LOGIN_REDIRECT_URL);
      return from(this.router.navigateByUrl(loginRedirectUrl));
    }
    return of(false);
  }
  /** Shows the login popup. */
  login(): Observable<AuthenticationResult> {
    const request: PopupRequest = {
      scopes: [],
      ...this.msalGuardConfig.authRequest,
      loginHint: this.msalSvc.instance.getActiveAccount()?.username,
    }

    return this.msalSvc.loginPopup(request);
  }

  /** Redirects to the logout page.  Easier on that apps then doing a popup. */
  logout(): Observable<void> {
    return this.msalSvc.logoutRedirect({
      postLogoutRedirectUri: '/'
    });
  }

  private aquireTokenSilent(fallbackToLoginPrompt: boolean) {
    const silentRequest: SilentRequest = {
      scopes: [],
      ...this.msalGuardConfig.authRequest,
      account: this.msalSvc.instance.getActiveAccount() || undefined,
      forceRefresh: false
    };
    // this will need to be converted to lastValueFrom
    return from(this.msalSvc.instance.acquireTokenSilent(silentRequest)).pipe(
      catchError(error => fallbackToLoginPrompt && error instanceof InteractionRequiredAuthError ? this.login() : throwError(error))
    );
  }

  /**
   * Unfortunately active account changes aren't streamed from msal.  This keeps it in sync with our activeAccount subject.
   * If no account is passed will use the first account in get all accounts.
   */
  private setActiveAccount(tokenAccountInfo?: AccountInfo | null | undefined): void {
    const activeAccount = tokenAccountInfo || this.msalSvc.instance.getAllAccounts()[0] || undefined;
    this.msalSvc.instance.setActiveAccount(activeAccount);
    this.activeAccountSubject.next(activeAccount);
  }
}


